import { useEffect, useState } from 'react';
import { api } from '@shared/api/client';
import { DEFAULT_DDW_CONFIG, type DdwConfig } from './config';

/**
 * Loads the published admin config for the DDW tool. Falls back to
 * the bundled defaults if no config is published yet, the request
 * fails, or the published config is missing required fields.
 *
 * Defensive: a malformed admin config never crashes the public tool.
 */
export function useDdwConfig(): { config: DdwConfig; loading: boolean } {
  const [config, setConfig] = useState<DdwConfig>(DEFAULT_DDW_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .getPublishedConfig<DdwConfig>('ddw')
      .then((res) => {
        if (cancelled) return;
        if (res.snapshot?.config) {
          setConfig(mergeConfig(DEFAULT_DDW_CONFIG, res.snapshot.config));
        }
      })
      .catch(() => {
        // Silently fall back to defaults; the public tool stays usable.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { config, loading };
}

function mergeConfig(base: DdwConfig, incoming: Partial<DdwConfig>): DdwConfig {
  return {
    content: { ...base.content, ...(incoming.content ?? {}) },
    redFlagWords: incoming.redFlagWords?.length ? incoming.redFlagWords : base.redFlagWords,
    calc: { ...base.calc, ...(incoming.calc ?? {}) },
    projection: {
      expectedExpansionsByFlex: {
        ...base.projection.expectedExpansionsByFlex,
        ...(incoming.projection?.expectedExpansionsByFlex ?? {}),
      },
      growthIntensityByFlex: {
        ...base.projection.growthIntensityByFlex,
        ...(incoming.projection?.growthIntensityByFlex ?? {}),
      },
    },
    scaleOut: incoming.scaleOut?.length ? incoming.scaleOut : base.scaleOut,
    scaleUp: incoming.scaleUp?.length ? incoming.scaleUp : base.scaleUp,
    adoptionQuestions: incoming.adoptionQuestions?.length
      ? incoming.adoptionQuestions
      : base.adoptionQuestions,
    spendTiers: incoming.spendTiers?.length ? incoming.spendTiers : base.spendTiers,
    research: { ...base.research, ...(incoming.research ?? {}) },
    vars: incoming.vars,
  };
}
